import { useCallback, useEffect, useRef, useState } from "react";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { useSearchParams } from "react-router-dom";
import { GridActionsCellItem } from "@mui/x-data-grid";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import { BonusDto, PAYMENT_FREQUENCY } from "../../../../models/bonus.dto";
import { AiOutlinePlus } from "react-icons/ai";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import {
	useDeleteBonusGroup,
	useGetBonusGroup,
	useGetBonusGroupSearch,
} from "../../../../services/bonus-group.service";
import {
	useDeleteBonus,
	useGetBonus,
	useGetBonusSearch,
} from "../../../../services/bonus.service";
import BonusGroupModalForm from "../modals/BonusGroupModalForm";
import EmployeeBonusModalForm from "../modals/EmployeeBonusModalForm";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import { useGetCompanyCurrencyConfig } from "../../../../services/company-currency.service";
import APISearchInput from "../../../../components/search/APISearchInput";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import { useGetEmployeeBand } from "../../../../services/employee-band.service";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { useGetEmployee } from "../../../../services/employee.service";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	PermissionChecker,
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import {
	bonusGroupColumns,
	bonusGroupColumnVisibility,
	bonusColumns,
	bonusColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import { ChevronDown, Upload } from "lucide-react";
import { GoUpload } from "react-icons/go";
import { BsDownload } from "react-icons/bs";
import UploadEmployeeBonuses from "../modals/UploadEmployeeBonuses";
import ExportBonusModal from "../modals/ExportBonusModal";

const BonusView = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();

	// Tab state
	const [activeTab, setActiveTab] = useState<"bonusGroups" | "bonuses">(
		"bonusGroups",
	);

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openBonusGroupModal, setOpenBonusGroupModal] = useState(false);
	const [openEmployeeBonusModal, setOpenEmployeeBonusModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [openUploadBonusModal, setOpenUploadBonusModal] = useState(false);
	const [openExportBonusModal, setOpenExportBonusModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [isUploadExportOpen, setIsUploadExportOpen] = useState(false);
	const uploadExportDropdownRef = useRef<HTMLDivElement | null>(null);
	const { formDataValues, setFormData } = useEditFormStore();
	const [searchParams, setSearchParams] = useSearchParams();

	// Column visibility for Bonus Groups
	const {
		tableKey: bonusGroupTableKey,
		columnVisibilityModel: bonusGroupColumnVisibilityModel,
		setColumnVisibilityModel: setBonusGroupColumnVisibilityModel,
	} = useColumnVisibilityModel(bonusGroupColumnVisibility);

	// Column visibility for Bonuses
	const {
		tableKey: bonusTableKey,
		columnVisibilityModel: bonusColumnVisibilityModel,
		setColumnVisibilityModel: setBonusColumnVisibilityModel,
	} = useColumnVisibilityModel(bonusColumnVisibility);

	const dropdownRef = useRef<HTMLDivElement | null>(null);

	// Bonus Groups table
	const { mutate: searchBonusGroupsMutate } = useGetBonusGroupSearch(currentCompany?.id as number);
	const searchBonusGroups = useCallback(
		(options: any, mutationOptions?: any) => {
			if (!currentCompany?.id) return;
			searchBonusGroupsMutate(
				{ companyId: currentCompany.id, ...options },
				mutationOptions,
			);
		},
		[currentCompany?.id, searchBonusGroupsMutate],
	);

	const {
		tableAPIRef: bonusGroupTableAPIRef,
		fetchedTableData: fetchedBonusGroupData,
		tableData: bonusGroupTableData,
		isLoading: bonusGroupLoading,
		pageCount: bonusGroupPageCount,
		setTableData: setBonusGroupTableData,
	} = useManageTableWithSearchParams<BonusDto>({
		fetchHook: (options) =>
			useGetBonusGroup(currentCompany?.id as number, options),
		options: {},
	});

	// Bonuses table
	const { mutate: searchBonuses } = useGetBonusSearch();
	const {
		tableAPIRef: bonusTableAPIRef,
		fetchedTableData: fetchedBonusData,
		tableData: bonusTableData,
		isLoading: bonusLoading,
		pageCount: bonusPageCount,
		setTableData: setBonusTableData,
	} = useManageTableWithSearchParams<BonusDto>({
		fetchHook: useGetBonus,
		options: {
			companyId: currentCompany?.id,
		},
	});

	// Fetched data for forms
	const { data: companyCurrencies } = useGetCompanyCurrencyConfig({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
	});

	const { data: bonusGroups } = useGetBonusGroup(
		currentCompany?.id as number,
		{
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		} as any,
	);

	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});

	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});

	const { data: employeeBands } = useGetEmployeeBand({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "name:asc" as any,
	});

	const handleClose = () => setAnchorEl(null);

	const handleOpenBonusGroupModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenBonusGroupModal(true);
	};

	const handleOpenEmployeeBonusModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenEmployeeBonusModal(true);
	};

	const handleTabChange = (tab: "bonusGroups" | "bonuses") => {
		setActiveTab(tab);
		const updated = new URLSearchParams(searchParams);
		updated.delete("page");
		updated.delete("pageSize");
		setSearchParams(updated);
	};

	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteBonusGroup } = useDeleteBonusGroup();
	const { mutate: deleteBonus } = useDeleteBonus();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		if (activeTab === "bonusGroups") {
			deleteBonusGroup(
				{ companyId: currentCompany?.id as number, id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		} else {
			deleteBonus(
				{ id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		}
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				uploadExportDropdownRef.current &&
				!uploadExportDropdownRef.current.contains(event.target as Node)
			) {
				setIsUploadExportOpen(false);
			}
		};

		if (isUploadExportOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isUploadExportOpen]);

	return (
		<div className="font-inter">
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={
							activeTab === "bonusGroups"
								? fetchedBonusGroupData
								: fetchedBonusData
						}
						setTableData={
							activeTab === "bonusGroups"
								? setBonusGroupTableData
								: setBonusTableData
						}
						mutate={
							activeTab === "bonusGroups" ? searchBonusGroups : searchBonuses
						}
					/>
					<TableFilter
						fields={
							activeTab === "bonusGroups"
								? [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Min Value", fieldName: "minValue" },
												{ title: "Max Value", fieldName: "maxValue" },
												{
													title: "Period Start Date",
													fieldName: "periodStartDate",
												},
												{
													title: "Period End Date",
													fieldName: "periodEndDate",
												},
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Payment Frequency",
											fieldName: "paymentFrequency",
											fieldType: "CHECKBOX_LIST",
											options: PAYMENT_FREQUENCY,
										},
										{
											title: "Period Start Date",
											fieldName: "periodStartDate",
											fieldType: "DATE_RANGE",
										},
										{
											title: "Period End Date",
											fieldName: "periodEndDate",
											fieldType: "DATE_RANGE",
										},
										{
											title: "Active",
											fieldName: "active",
											fieldType: "YES_NO",
										},
									]
								: [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Min Value", fieldName: "minValue" },
												{ title: "Max Value", fieldName: "maxValue" },
												{
													title: "Period Start Date",
													fieldName: "periodStartDate",
												},
												{
													title: "Period End Date",
													fieldName: "periodEndDate",
												},
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Payment Frequency",
											fieldName: "paymentFrequency",
											fieldType: "CHECKBOX_LIST",
											options: PAYMENT_FREQUENCY,
										},
										{
											title: "Bonus Group",
											fieldName: "groupId",
											fieldType: "INPUT_FIELD",
										},
										{
											title: "Active",
											fieldName: "active",
											fieldType: "YES_NO",
										},
									]
						}
					/>
					<ManageTableColumns
						columns={
							activeTab === "bonusGroups"
								? (bonusGroupColumns as any)
								: (bonusColumns as any)
						}
						defaultVisibilityModel={
							activeTab === "bonusGroups"
								? bonusGroupColumnVisibility
								: bonusColumnVisibility
						}
						visibilityModel={
							activeTab === "bonusGroups"
								? bonusGroupColumnVisibilityModel
								: bonusColumnVisibilityModel
						}
						setVisibilityModel={
							activeTab === "bonusGroups"
								? setBonusGroupColumnVisibilityModel
								: setBonusColumnVisibilityModel
						}
						tableApiRef={
							activeTab === "bonusGroups"
								? bonusGroupTableAPIRef
								: bonusTableAPIRef
						}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					{activeTab === "bonusGroups" && (
						<PermissionChecker
							permissionRule="OR"
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setOpenUploadBonusModal(true)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<GoUpload className="mr-2 text-[17px] text-gray-500" />
								Upload Bonus Groups
							</button>
						</PermissionChecker>
					)}

					{/* Export - Only show for Employee Bonuses tab */}
					{activeTab === "bonuses" && (
						<button
							onClick={() => setOpenExportBonusModal(true)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<BsDownload className="mr-2 text-[18px] text-gray-500" />
							Export Bonus
						</button>
					)}

					<PermissionChecker
						permissionRule="OR"
						permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
					>
						{activeTab === "bonusGroups" ? (
							<button
								onClick={handleOpenBonusGroupModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Bonus Group
							</button>
						) : (
							<button
								onClick={handleOpenEmployeeBonusModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Employee Bonus
							</button>
						)}
					</PermissionChecker>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 mb-6">
				<button
					onClick={() => handleTabChange("bonusGroups")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "bonusGroups"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Bonus Groups
				</button>
				<button
					onClick={() => handleTabChange("bonuses")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "bonuses"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Employee Bonuses
				</button>
			</div>

			{activeTab === "bonusGroups" ? (
				<TanStackEmployeeTable
					ref={bonusGroupTableAPIRef}
					tableId={TableId.BONUS_GROUPS}
					data={bonusGroupTableData}
					columns={bonusGroupColumns}
					columnVisibility={bonusGroupColumnVisibilityModel}
					onColumnVisibilityChange={setBonusGroupColumnVisibilityModel}
					loading={bonusGroupLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={bonusGroupColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={bonusGroupPageCount}
					apiRef={bonusGroupTableAPIRef}
				/>
			) : (
				<TanStackEmployeeTable
					ref={bonusTableAPIRef}
					tableId={TableId.BONUS}
					data={bonusTableData}
					columns={bonusColumns}
					columnVisibility={bonusColumnVisibilityModel}
					onColumnVisibilityChange={setBonusColumnVisibilityModel}
					loading={bonusLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={bonusColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={bonusPageCount}
					apiRef={bonusTableAPIRef}
				/>
			)}

			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={
					activeTab === "bonusGroups"
						? setOpenBonusGroupModal
						: setOpenEmployeeBonusModal
				}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openBonusGroupModal && (
				<BonusGroupModalForm
					open={openBonusGroupModal}
					setOpen={setOpenBonusGroupModal}
					title={isEditForm ? "Update Bonus Group" : "Add New Bonus Group"}
					isEditForm={isEditForm}
					tableAPIRef={bonusGroupTableAPIRef}
					companyCurrencies={companyCurrencies?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openEmployeeBonusModal && (
				<EmployeeBonusModalForm
					open={openEmployeeBonusModal}
					setOpen={setOpenEmployeeBonusModal}
					title={
						isEditForm ? "Update Employee Bonus" : "Add New Employee Bonus"
					}
					isEditForm={isEditForm}
					tableAPIRef={bonusTableAPIRef}
					employees={employees?.data || []}
					employeeBands={employeeBands?.data || []}
					payPeriods={payPeriods?.data || []}
					bonusGroups={bonusGroups?.data || []}
					reloadTableData={reloadTableData}
					currencies={companyCurrencies?.data || []}
				/>
			)}
			{openUploadBonusModal && (
				<UploadEmployeeBonuses
					open={openUploadBonusModal}
					setOpen={setOpenUploadBonusModal}
				/>
			)}
			{openExportBonusModal && (
				<ExportBonusModal
					open={openExportBonusModal}
					setOpen={setOpenExportBonusModal}
					companyId={currentCompany?.id as number}
				/>
			)}
		</div>
	);
};

export default BonusView;